/**
 * @description Super Admin Channel Partners list (LYN-288)
 *
 * Key features:
 * - Lists channel_partners only (never LynxPay-direct / platform_config)
 * - Search by name, slug, or owner email
 * - Status filter + slug / domain status columns
 * - Create entry → /channel-partners/new (button label: Create)
 *
 * @dependencies
 * - adminApi: getChannelPartners()
 * - react-router-dom: useNavigate
 *
 * @notes
 * - Empty list until a CP is created is correct
 * - Tenants nav remains SOs; this is a peer surface
 * - Host column copy is current-env only via getChannelPartnerHostPreview
 * - CP admin cannot reach this portal (AuthContext requires superadmin)
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Handshake, AlertCircle } from 'lucide-react';
import { getChannelPartners } from '../../services/api/adminApi';
import { getChannelPartnerHostPreview } from '../../config/domains';

function ChannelPartnerList() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const result = await getChannelPartners();
        if (result.success) {
          setPartners(result.data);
        }
      } catch (err) {
        setError(err.response?.data?.error?.message || 'Failed to load channel partners');
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  const filtered = useMemo(() => {
    return partners.filter((partner) => {
      const haystack = [
        partner.name,
        partner.slug,
        partner.owner?.email,
      ].join(' ').toLowerCase();
      const matchesSearch = !search || haystack.includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && partner.isActive) ||
        (statusFilter === 'inactive' && !partner.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [partners, search, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-800 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-gray-900 font-medium mb-1">Failed to load channel partners</p>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">All Channel Partners</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {partners.length} total
          </p>
        </div>
        <button
          onClick={() => navigate('/channel-partners/new')}
          className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-500 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, slug, or owner email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <Handshake className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {search || statusFilter !== 'all'
              ? 'No channel partners match your filters'
              : 'No channel partners yet. Create one to stand up a CP tenant.'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Partner</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((partner) => (
                <tr
                  key={partner.id}
                  onClick={() => navigate(`/channel-partners/${partner.id}`)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{partner.name}</p>
                    <p className="text-xs text-gray-400">{getChannelPartnerHostPreview(partner.slug)}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-mono">{partner.slug}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-700">
                      {partner.owner?.firstName} {partner.owner?.lastName}
                    </p>
                    <p className="text-xs text-gray-400">{partner.owner?.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      partner.isActive
                        ? 'bg-green-50 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {partner.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {partner.customDomainStatus || 'none'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ChannelPartnerList;
